import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Gravatar from 'react-gravatar';
// @material-ui/icons
import Check from '@material-ui/icons/Check';
// @material-ui/core components
import Checkbox from '@material-ui/core/Checkbox';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import GridItem from 'components/UI/Grid/GridItem';
import CardAvatar from 'components/UI/Card/CardAvatar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import PermissionStyles from 'assets/jss/UserPermission';
import { USER } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import PermissionGroupSelect from 'components/Permission/Select';
import { withTranslation } from 'react-i18next';
import { getUserPhotoUrl } from 'helpers/functions';

/**
 * @class
 * @extends React.Component
 */
class UserPermission extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      permissions: props.user.permissions,
      permissionsGroups: props.user.permissionsGroups,
    };
  }

  /**
   * @description
   * Handle update permissions.
   *
   * @param {Object} perm - Permission to update.
   */
  handleUpdate = perm => {
    this.setState({
      permissions: perm,
    });
  };

  /**
   * @description
   * Handler adding/removing groups.
   *
   * @param {Object} group - Group to add or remove.
   * @returns {undefined} No return.
   */
  handleAddGroup = group => {
    const { permissionsGroups } = this.state;
    let newGroup;

    if (permissionsGroups.some(g => g.id === group.node.id)) {
      newGroup = permissionsGroups.filter(
        permGroup => permGroup.id !== group.node.id,
      );
    } else {
      newGroup = [group.node, ...permissionsGroups];
    }
    this.setState({
      permissionsGroups: newGroup,
    });
  };

  /**
   * @description
   * Handler disabled state of button.
   *
   * @param {Object} statePermissions - Permissions in state.
   * @param {Object} userPermissions - User permissions.
   * @returns {Array} Permissions.
   */
  handleCheckChanges = (statePermissions, userPermissions) => {
    const localPerm = statePermissions.filter(
      el => !userPermissions.some(x => x.id === el.id),
    );
    const serverPerm = userPermissions.filter(
      el => !statePermissions.some(x => x.id === el.id),
    );
    return [...localPerm, ...serverPerm];
  };

  /**
   * @description
   * Handle redirect when cancel update user permissions.
   */
  handleCancel = () => {
    const history = this.context;
    history.push(USER.LIST);
  };

  /**
   * @description
   * Render User Permissions component.
   */
  render() {
    const { user, groups, classes, permissionUpdate, t } = this.props;
    const { permissions, permissionsGroups } = this.state;

    const notChangedPermission =
      this.handleCheckChanges(permissions, user.permissions).length === 0;
    const notChangedGroups =
      this.handleCheckChanges(permissionsGroups, user.permissionsGroups)
        .length === 0;

    return (
      <React.Fragment>
        <GridContainer alignItems="center">
          <GridItem xs={12} sm={6}>
            <h3>{t('user.permission.title')}</h3>
          </GridItem>
          <GridItem xs={12} sm={6}>
            <div className={classes.avatarContainer}>
              <CardAvatar profile>
                {user.photo ? (
                  <img src={getUserPhotoUrl(user.photo)} alt="Avatar" />
                ) : (
                  <Gravatar email={user.email || user.fullname} />
                )}
              </CardAvatar>
              <h4
                id={`user-fullname-${user.fullname}`}
                className={classes.cardTitle}
              >
                {user.fullname}
              </h4>
            </div>
          </GridItem>
        </GridContainer>

        <Card>
          <CardBody className={classes.container}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={3}>
                <div style={{ marginBottom: 36 }}>
                  <h6>{t('user.permission.group')}</h6>
                  {groups.edges.map(g => (
                    <FormControlLabel
                      key={`permission-group-${g.node.id}`}
                      id={`permission-group-${g.node.id}`}
                      className={classes.checkInputs}
                      control={
                        <Checkbox
                          onClick={() => this.handleAddGroup(g)}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                          }}
                          checked={permissionsGroups.some(
                            permGroup => permGroup.id === g.node.id,
                          )}
                        />
                      }
                      label={g.node.name}
                    />
                  ))}
                </div>
              </GridItem>
              <GridItem xs={12} sm={12} md={9}>
                <PermissionGroupSelect
                  selectedGroups={permissionsGroups}
                  selectedPermissions={permissions}
                  permissionUpdate={p => this.handleUpdate(p)}
                />
              </GridItem>
            </GridContainer>

            <div className={classes.contentFooter}>
              <GridContainer>
                <GridItem xs={12} sm={6}>
                  <Button
                    onClick={() => this.handleCancel()}
                    id="cancelBtn"
                    simple
                    color="primary"
                  >
                    {t('common.cancel')}
                  </Button>
                </GridItem>
                <GridItem xs={12} sm={6}>
                  <div className={classes.pullRight}>
                    <Button
                      onClick={() =>
                        permissionUpdate({
                          permissionIds: permissions.map(
                            permission => permission.id,
                          ),
                          permissionGroupIds: permissionsGroups.map(
                            permissionsGroup => permissionsGroup.id,
                          ),
                        })
                      }
                      disabled={notChangedPermission && notChangedGroups}
                      color={
                        notChangedPermission && notChangedGroups
                          ? null
                          : 'primary'
                      }
                      id="savePermissions"
                    >
                      {t('user.permission.save')}
                    </Button>
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </CardBody>
        </Card>
      </React.Fragment>
    );
  }
}

/**
 * UserPermission PropTypes
 * @param classes - prop styles received from withStyles
 * @param users - prop users received from container
 * @param groups - prop permissions groups received from container
 * @param t - prop from HOC with string translations
 * @callback permissionUpdate - callback called when click save permissions
 */

UserPermission.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  user: PropTypes.instanceOf(Object).isRequired,
  groups: PropTypes.instanceOf(Object).isRequired,
  permissionUpdate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const UserPermissionWrapper = props => <UserPermission {...props} />;

export default withTranslation()(
  withStyles(PermissionStyles)(UserPermissionWrapper),
);
